/* The base map the job overview stands on: MapLibre over a self-hosted
 * Protomaps basemap read from a PMTiles archive, desaturated, with its controls
 * and its attribution, and the satellite ground a view can switch to.
 *
 * What this deliberately is not: anything drawn over the basemap. A view adds
 * its own sources and layers once the map has loaded.
 */
import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { layersWithCustomTheme } from "protomaps-themes-base";
import { useImagery } from "../../api/tilesToken";
import { slateSteelTheme } from "../../lib/basemapTheme";
// Sets MapLibre's worker URL, which every map needs before it is constructed.
import "../../lib/maplibre";

/* PMTiles is a single archive read over HTTP Range requests rather than a tile
 * server (`02` -> PMTiles serving). MapLibre reaches it through a protocol
 * handler, registered once at module scope because addProtocol is global to the
 * library and registering it per mount would replace the handler under any map
 * already using it. */
maplibregl.addProtocol("pmtiles", new Protocol().tile);

const BASEMAP_SOURCE = "basemap";

/* OpenStreetMap data under ODbL. The attribution is a licence condition, not a
 * courtesy, so the control below is added rather than suppressed. */
const BASEMAP_ATTRIBUTION =
  '<a href="https://openstreetmap.org/copyright" target="_blank" rel="noreferrer">&copy; OpenStreetMap</a>';

/* The basemap style: our glyphs, the archive at `basemapUrl` and the Slate/Steel
 * theme. Every map that stands on the basemap takes its style from here; one
 * that needs its own controls builds its map around this rather than calling
 * createBasemap.
 *
 * `basemapUrl` is the archive `/api/tiles/token` names (`useBasemapUrl`), read
 * through Front Door. Null draws no ground at all, and the caller says why. */
export function basemapStyle(basemapUrl: string | null): maplibregl.StyleSpecification {
  return {
    version: 8,
    /* THE GLYPHS ARE SERVED FROM OUR OWN ORIGIN, and that is what makes
     * labels possible at all. Protomaps' label layers need a glyph endpoint,
     * and the only hosted one is a third-party origin the CSP refuses:
     * `connect-src` names this environment's tile host and nothing else, and
     * it is written in provision/appconfig.sh rather than in a planning doc
     * (`10` 2.2 establishes the hostname, not the directive). public/fonts
     * holds the Latin ranges of the faces the theme asks for, so the
     * request is same-origin and the CSP needs no widening.
     *
     * Only the Latin ranges are carried: 0-255, 256-511 and 8192-8447 of Noto
     * Sans Regular, Medium and Italic. A label needing a range that is absent
     * draws nothing and logs a 404 rather than breaking the map, so a
     * territory outside Latin script needs its ranges added -
     * provision/README.md says how.
     *
     * Same-origin, so `connect-src 'self'` already covers it. {fontstack}
     * and {range} are MapLibre's own placeholders; it URL-encodes the
     * fontstack, which is why the directories under public/fonts carry the
     * face names verbatim, spaces and all. */
    glyphs: "/fonts/{fontstack}/{range}.pbf",
    sources:
      basemapUrl === null
        ? {}
        : {
            [BASEMAP_SOURCE]: {
              type: "vector",
              url: `pmtiles://${basemapUrl}`,
              attribution: BASEMAP_ATTRIBUTION,
            },
          },
    /* Grayscale carrying Steel's hue at its own lightness, not one of the
     * five named themes. `01` and `14` 9.3 both say "Basemap desaturated to
     * Slate/Steel", and none of the named themes is that: grayscale is
     * neutral and light is saturated. basemapTheme.ts derives it and carries
     * the argument, including why a mix toward Steel is the wrong operation.
     * Only data stays saturated, because the risk ramp is the one thing on
     * this screen carrying meaning through colour. */
    layers: basemapUrl === null ? [] : layersWithCustomTheme(BASEMAP_SOURCE, slateSteelTheme(), "en"),
  };
}

/* The basemap stays near-desaturated so colour on a map reads as data rather
 * than as terrain (FOUNDATIONS 06, Product Screens slide 23); basemapTheme.ts
 * is where that is decided and holds the figure.
 *
 * The map's style is basemapStyle's; this adds the controls. */
export function createBasemap(
  container: HTMLElement,
  camera: { center: [number, number]; zoom: number },
  basemapUrl: string | null,
): maplibregl.Map {
  const map = new maplibregl.Map({
    container,
    style: basemapStyle(basemapUrl),
    center: camera.center,
    zoom: camera.zoom,
    attributionControl: false,
  });
  map.addControl(new maplibregl.NavigationControl({ showCompass: true }), "top-right");
  map.addControl(new maplibregl.ScaleControl({ unit: "imperial" }), "bottom-left");
  /* Suppressed in the constructor and added here so it is compact by default -
   * the attribution has to be present, it does not have to be a banner. */
  map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
  return map;
}

/* USDA NAIP, the same PMTiles shape as the vector basemap but raster, one
 * archive per grid cell. `02` section Aerial imagery carries the decision:
 * public domain, credit requested, self-hosted, and not Mapbox Satellite, whose
 * terms restrict their tiles to their own SDKs.
 *
 * The cells are tiled offline by a worker job rather than reached live, which
 * is what keeps Planetary Computer out of the browser entirely, and
 * `/api/tiles/token` names each cell's current archive. A cell's source and
 * layer share one id, the prefix and the archive name. */
const IMAGERY_PREFIX = "imagery-";

/* Public domain, and the credit is requested rather than optional - USDA FSA's
 * policy asks for exactly this wording. Their symbol may not be used and is
 * not. */
const IMAGERY_ATTRIBUTION = "U.S. Department of Agriculture, Farm Service Agency";

/* The basemap's state and country lines (protomaps-themes-base's layer ids),
 * lifted above the imagery so borders still read on Satellite. Roads stay
 * beneath it, where the imagery shows the real ones. Off Satellite they return
 * to their place in the style, beneath the bridges. */
const BOUNDARY_LAYERS = ["boundaries_country", "boundaries"];

/* The territory beneath the cells' zoom is USGS The National Map's imagery,
 * public domain, and this is the credit USGS asks for. */
const US_IMAGERY_ATTRIBUTION = "Map services and data available from U.S. Geological Survey, National Geospatial Program";

/* The satellite ground under a map, shown while `on`.
 *
 * NOT IN THE INITIAL STYLE. A cell is added the first time Satellite is on with
 * the cell in view: a raster source declared up front makes MapLibre fetch its
 * archive's header on every page load even with the layer hidden, and every
 * cell the account holds is not something to touch on the chance somebody
 * looks at one.
 *
 * After that a cell stays, and the toggle is visibility alone: tearing the
 * source down would discard its tile cache and re-fetch everything on the next
 * switch. Neither add nor toggle moves the camera, which is what keeps this
 * inside "preserve position" (FOUNDATIONS 06, slide 8). */
export function useSatellite(map: maplibregl.Map | null, loaded: boolean, on: boolean): void {
  // The map's view, kept as it moves, so the cells that come into it are added.
  const [view, setView] = useState<maplibregl.LngLatBounds | null>(null);
  useEffect(() => {
    if (!map || !loaded) return;
    const update = () => setView(map.getBounds());
    update();
    map.on("moveend", update);
    return () => {
      map.off("moveend", update);
    };
  }, [map, loaded]);

  const imagery = useImagery();
  // Each boundary layer lifted above the imagery, with the layer it sat beneath.
  const lifted = useRef<[string, string | undefined][]>([]);
  useEffect(() => {
    if (!map || !loaded) return;
    if (on && view) {
      /* Below the first label layer, so place names stay readable over canopy,
       * and below every layer a view adds on load, which sits above everything
       * the style arrived with. */
      const firstLabel = map.getStyle().layers.find((l) => l.type === "symbol");
      /* The territory first and once, so every cell, inserted at the same
       * place later, lands above it: the cells' finer ground covers the
       * territory's wherever both are held. */
      if (imagery.us !== null) {
        const id = `${IMAGERY_PREFIX}us`;
        if (!map.getSource(id)) {
          map.addSource(id, {
            type: "raster",
            url: `pmtiles://${imagery.us}`,
            tileSize: 256,
            attribution: US_IMAGERY_ATTRIBUTION,
          });
          map.addLayer({ id, type: "raster", source: id }, firstLabel?.id);
        }
      }
      for (const cell of imagery.cells) {
        const id = `${IMAGERY_PREFIX}${cell.archive}`;
        const [west, south, east, north] = cell.bounds;
        const inView = west < view.getEast() && east > view.getWest() && south < view.getNorth() && north > view.getSouth();
        if (!inView || map.getSource(id)) continue;
        map.addSource(id, {
          type: "raster",
          url: `pmtiles://${cell.url}`,
          tileSize: 256,
          attribution: IMAGERY_ATTRIBUTION,
        });
        /* Each cell's own correction, measured against the imagery around it
         * when the cell was built, so its edge does not show. A per-pixel
         * colour adjustment: the imagery's detail is untouched. */
        const tone = cell.correction;
        map.addLayer(
          {
            id,
            type: "raster",
            source: id,
            paint: tone
              ? {
                  "raster-brightness-min": tone.brightness_min,
                  "raster-brightness-max": tone.brightness_max,
                  "raster-contrast": tone.contrast,
                  "raster-saturation": tone.saturation,
                }
              : {},
          },
          firstLabel?.id,
        );
      }
      for (const boundary of BOUNDARY_LAYERS) {
        if (!map.getLayer(boundary)) continue;
        /* Where it sat in the style, kept before its first lift: the id of the
         * layer that followed it, or undefined where it was the top. */
        if (!lifted.current.some(([id]) => id === boundary)) {
          const layers = map.getStyle().layers;
          lifted.current.push([boundary, layers[layers.findIndex((l) => l.id === boundary) + 1]?.id]);
        }
        map.moveLayer(boundary, firstLabel?.id);
      }
    }
    if (!on) {
      /* Last lifted first, so each lands beneath a layer already back in place. */
      for (const [boundary, before] of [...lifted.current].reverse()) {
        if (map.getLayer(boundary)) map.moveLayer(boundary, before);
      }
      lifted.current = [];
    }
    for (const layer of map.getStyle().layers) {
      if (layer.id.startsWith(IMAGERY_PREFIX)) map.setLayoutProperty(layer.id, "visibility", on ? "visible" : "none");
    }
  }, [map, loaded, on, imagery, view]);
}
